import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();

  const BACKEND_URL = window._env_?.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (token) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/user/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Identify user in PostHog with their unique ID and properties
        if (posthog && userData) {
          try {
            posthog.identify(userData.id, {
              email: userData.email,
              name: userData.name,
              created_at: userData.created_at,
              last_login: userData.last_login
            });
          } catch (error) {
            console.warn('PostHog identify failed:', error);
          }
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (authToken, userData) => {
    setToken(authToken);
    setUser(userData);
    localStorage.setItem('authToken', authToken);
    
    // Identify user in PostHog when they log in
    if (posthog && userData) {
      try {
        posthog.identify(userData.id, {
          email: userData.email,
          name: userData.name,
          created_at: userData.created_at,
          last_login: userData.last_login
        });
        
        // Track the login event
        posthog.capture('user_logged_in', {
          login_method: 'google', // assuming Google login based on the codebase
          user_id: userData.id,
          email: userData.email
        });
      } catch (error) {
        console.warn('PostHog login tracking failed:', error);
      }
    }
  };

  const logout = () => {
    // Track logout event before resetting
    if (posthog && user) {
      try {
        posthog.capture('user_logged_out', {
          user_id: user.id
        });
      } catch (error) {
        console.warn('PostHog logout tracking failed:', error);
      }
    }
    
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    
    // Reset PostHog to unlink future events from this user
    if (posthog) {
      try {
        posthog.reset();
      } catch (error) {
        console.warn('PostHog reset failed:', error);
      }
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    isModerator: !!user?.is_moderator,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
